export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
export type TicketCategory = 'technical' | 'billing' | 'account' | 'content' | 'frequency' | 'moderation' | 'other';

export interface TicketMessage {
  id: number;
  from: 'support' | 'customer';
  author: string;
  content: string;
  date: string;
}

export interface SupportTicket {
  id: number;
  ticketNumber: string;
  radioName: string;
  frequency: string;
  subject: string;
  description: string;
  priority: TicketPriority;
  status: TicketStatus;
  assignedTo: string | null;
  category: TicketCategory;
  createdAt: string;
  updatedAt: string;
  messages: TicketMessage[];
}

export const priorityLabels: Record<TicketPriority, string> = {
  low: 'Baja',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

export const statusLabels: Record<TicketStatus, string> = {
  open: 'Abierto',
  in_progress: 'En Progreso',
  waiting_customer: 'Esperando Cliente',
  resolved: 'Resuelto',
  closed: 'Cerrado',
};

export const categoryLabels: Record<TicketCategory, string> = {
  technical: 'Problema Técnico',
  billing: 'Facturación',
  account: 'Cuenta',
  content: 'Contenido',
  frequency: 'Frecuencia',
  moderation: 'Moderación',
  other: 'Otro',
};

export const agentList = [
  { id: 'SA-001', name: 'Carlos Mendoza', role: 'Super Admin' },
  { id: 'SA-002', name: 'María Gutiérrez', role: 'Admin Senior' },
  { id: 'SA-003', name: 'Andrés López', role: 'Soporte' },
  { id: 'SA-004', name: 'Diana Ríos', role: 'Soporte' },
  { id: 'SA-005', name: 'Luis Hernández', role: 'Soporte' },
  { id: 'SA-006', name: 'Sofía Vargas', role: 'Soporte' },
];

export const statusOptions: TicketStatus[] = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
export const priorityOptions: TicketPriority[] = ['low', 'normal', 'high', 'urgent'];
export const categoryOptions: TicketCategory[] = ['technical', 'billing', 'account', 'content', 'frequency', 'moderation', 'other'];

export const supportTickets: SupportTicket[] = [
  {
    id: 1,
    ticketNumber: 'TKT-2026-0892',
    radioName: 'Radio Nueva Actitud',
    frequency: '87.90 RFW',
    subject: 'Stream se corta cada 30 minutos',
    description: 'Desde ayer el stream se interrumpe automáticamente cada media hora. Los oyentes reportan que se desconectan constantemente. Ya reiniciamos el servidor pero el problema persiste.',
    priority: 'high',
    status: 'open',
    assignedTo: null,
    category: 'technical',
    createdAt: '2026-06-09 09:15:00',
    updatedAt: '2026-06-09 09:15:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Nueva Actitud', content: 'Hola, desde ayer 8 de junio nuestro stream se corta exactamente cada 30 minutos. Los oyentes se quejan porque se van del stream. Reiniciamos el encoder y el servidor pero no se soluciona. ¿Nos pueden ayudar urgente?', date: '2026-06-09 09:15:00' },
    ],
  },
  {
    id: 2,
    ticketNumber: 'TKT-2026-0891',
    radioName: 'Stereo Visión',
    frequency: '94.70 RFW',
    subject: 'Cobro duplicado en factura de junio',
    description: 'En el extracto bancario aparece dos veces el cobro de $49 correspondiente a junio 2026. Necesitamos que revisen y hagan la devolución del cargo duplicado.',
    priority: 'high',
    status: 'in_progress',
    assignedTo: 'SA-002',
    category: 'billing',
    createdAt: '2026-06-08 14:30:00',
    updatedAt: '2026-06-09 08:45:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Stereo Visión', content: 'Hola equipo, revisando los movimientos del banco veo que me cobraron $49 dos veces este mes. El 10 de junio sería mi fecha normal de facturación pero hay un segundo cargo del mismo monto. Adjunto captura del extracto. ¿Pueden revisarlo?', date: '2026-06-08 14:30:00' },
      { id: 2, from: 'support', author: 'María Gutiérrez', content: 'Hola, gracias por reportarlo. Ya revisé en el sistema y efectivamente veo dos transacciones con fecha 10/06. Voy a escalar con el equipo de pagos para que hagan la reversión del duplicado. Te mantengo al tanto.', date: '2026-06-09 08:45:00' },
    ],
  },
  {
    id: 3,
    ticketNumber: 'TKT-2026-0890',
    radioName: 'TechBeats',
    frequency: '88.50 RFW',
    subject: 'No puedo cambiar la foto de perfil',
    description: 'Intento subir una nueva foto de perfil pero siempre me da error "Tamaño de archivo excedido". La foto pesa 800 KB, debería estar dentro del límite de mi plan Pro.',
    priority: 'normal',
    status: 'waiting_customer',
    assignedTo: 'SA-004',
    category: 'account',
    createdAt: '2026-06-07 16:20:00',
    updatedAt: '2026-06-08 11:10:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin TechBeats', content: 'Hola, necesito actualizar el logo de la radio pero cuando intento subir la imagen me sale "Tamaño de archivo excedido". La imagen es PNG de 800 KB. Tengo plan Pro así que debería aceptar hasta 2 MB. ¿Es un bug?', date: '2026-06-07 16:20:00' },
      { id: 2, from: 'support', author: 'Diana Ríos', content: 'Hola, revisé tu cuenta y el límite para plan Pro es efectivamente 2 MB. ¿Podrías confirmarme las dimensiones de la imagen? A veces archivos con muchas capas o metadata inflada pueden causar ese error aunque pesen menos de 2 MB. Probá guardarla como JPG calidad 90% y volvé a intentar.', date: '2026-06-08 11:10:00' },
    ],
  },
  {
    id: 4,
    ticketNumber: 'TKT-2026-0889',
    radioName: 'Radio Élite Digital',
    frequency: '91.30 RFW',
    subject: 'Solicitud de upgrade a Premium',
    description: 'Queremos hacer el upgrade de Pro a Premium. Necesitamos más storage para los podcasts y lives ilimitadas. ¿Cómo iniciamos el proceso?',
    priority: 'low',
    status: 'resolved',
    assignedTo: 'SA-002',
    category: 'billing',
    createdAt: '2026-06-06 10:00:00',
    updatedAt: '2026-06-07 15:30:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Élite Digital', content: 'Hola, estamos en plan Pro pero necesitamos más espacio y lives ilimitadas. Queremos pasar a Premium. ¿Cómo lo hacemos?', date: '2026-06-06 10:00:00' },
      { id: 2, from: 'support', author: 'María Gutiérrez', content: 'Hola, claro. El upgrade es inmediato. Desde tu panel de administración en Ajustes > Plan podés seleccionar Premium. El cambio se aplica al instante y se prorratea el cobro del mes. Si tenés dudas avisame.', date: '2026-06-06 14:20:00' },
      { id: 3, from: 'customer', author: 'Admin Radio Élite Digital', content: 'Perfecto, ya lo hice y funcionó. ¡Gracias!', date: '2026-06-07 15:30:00' },
    ],
  },
  {
    id: 5,
    ticketNumber: 'TKT-2026-0888',
    radioName: 'Radio Deportiva Total',
    frequency: '101.70 RFW',
    subject: 'Problema con frecuencia asignada',
    description: 'Al sintonizar 101.70 RFW se cruza con otra emisora local en algunas zonas de la ciudad. Los oyentes reportan interferencia.',
    priority: 'urgent',
    status: 'open',
    assignedTo: null,
    category: 'frequency',
    createdAt: '2026-06-09 10:05:00',
    updatedAt: '2026-06-09 10:05:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Deportiva Total', content: 'URGENTE: Varios oyentes del centro y sur de la ciudad reportan que en 101.70 RFW escuchan otra emisora mezclada con nuestra señal. Esto es grave porque tenemos un partido importante hoy a las 20:00. Necesitamos solución ya.', date: '2026-06-09 10:05:00' },
    ],
  },
  {
    id: 6,
    ticketNumber: 'TKT-2026-0887',
    radioName: 'Radio Urbana 101',
    frequency: '103.50 RFW',
    subject: 'Mensajes ofensivos en cabina',
    description: 'Tenemos un usuario que está spameando mensajes con insultos en la cabina. Ya lo bloqueamos desde nuestro panel pero crea cuentas nuevas. Necesitamos bloqueo por IP.',
    priority: 'high',
    status: 'in_progress',
    assignedTo: 'SA-003',
    category: 'moderation',
    createdAt: '2026-06-08 20:15:00',
    updatedAt: '2026-06-09 07:30:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Urbana 101', content: 'Hola, tenemos un troll que no para de crear cuentas para insultar en la cabina. Ya bloqueamos 4 usuarios distintos pero siempre vuelve. Los oyentes se están quejando. ¿Pueden hacer un bloqueo por IP? Adjunto capturas de los mensajes.', date: '2026-06-08 20:15:00' },
      { id: 2, from: 'support', author: 'Andrés López', content: 'Hola, ya revisé las cuentas y efectivamente todas vienen de la misma IP: 87.221.45.90. Procedí a bloquearla a nivel plataforma. También eliminé los mensajes ofensivos. Cualquier otra cuenta nueva de esa IP será rechazada automáticamente.', date: '2026-06-09 07:30:00' },
    ],
  },
  {
    id: 7,
    ticketNumber: 'TKT-2026-0886',
    radioName: 'Clásica FM',
    frequency: '99.10 RFW',
    subject: 'Consulta sobre límites del plan Free',
    description: 'Somos una radio nueva y queremos entender bien qué incluye el plan gratuito. ¿Cuánto storage tenemos? ¿Podemos hacer lives?',
    priority: 'low',
    status: 'closed',
    assignedTo: 'SA-005',
    category: 'account',
    createdAt: '2026-06-05 11:30:00',
    updatedAt: '2026-06-06 09:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Clásica FM', content: 'Hola, arrancamos hace poco y tenemos plan Free. Queremos saber bien los límites: ¿cuánto storage para fotos y podcasts? ¿Se pueden hacer lives? ¿Hay límite de oyentes? Gracias.', date: '2026-06-05 11:30:00' },
      { id: 2, from: 'support', author: 'Luis Hernández', content: 'Hola, el plan Free incluye: 5 GB storage, 1 live semanal de hasta 2 horas, cabina básica con 50 mensajes/día, sin límite de oyentes. Si necesitás más, el plan Pro ($19/mes) te da 50 GB, lives ilimitadas y cabina full.', date: '2026-06-06 09:00:00' },
    ],
  },
  {
    id: 8,
    ticketNumber: 'TKT-2026-0885',
    radioName: 'Radio Marítima',
    frequency: '89.50 RFW',
    subject: 'Error al publicar programa en vivo',
    description: 'Cuando intento iniciar un live desde el broadcast center me sale "Error 503 — Service Unavailable". Probé en Chrome y Firefox, mismo error.',
    priority: 'normal',
    status: 'open',
    assignedTo: null,
    category: 'technical',
    createdAt: '2026-06-09 07:40:00',
    updatedAt: '2026-06-09 07:40:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Marítima', content: 'Buen día, estoy tratando de lanzar el programa de la mañana y el broadcast center me tira error 503. Ya probé con otro navegador y otra conexión. ¿Hay algún problema con los servidores?', date: '2026-06-09 07:40:00' },
    ],
  },
  {
    id: 9,
    ticketNumber: 'TKT-2026-0884',
    radioName: 'Jazz Club Radio',
    frequency: '92.80 RFW',
    subject: 'Reactivación de cuenta suspendida',
    description: 'Nuestra radio fue suspendida por falta de pago pero ya regularizamos la situación. Queremos reactivar la cuenta lo antes posible.',
    priority: 'high',
    status: 'waiting_customer',
    assignedTo: 'SA-002',
    category: 'billing',
    createdAt: '2026-06-07 09:00:00',
    updatedAt: '2026-06-08 13:15:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Jazz Club Radio', content: 'Hola, nos suspendieron la radio. Ya hicimos el pago pendiente. ¿Cuánto tarda la reactivación? Tenemos programación para hoy a la tarde.', date: '2026-06-07 09:00:00' },
      { id: 2, from: 'support', author: 'María Gutiérrez', content: 'Hola, confirmo que recibimos el pago. Para reactivar necesito que me confirmes el cambio de método de pago a tarjeta para evitar futuros inconvenientes. ¿Tenés los datos de la tarjeta a mano?', date: '2026-06-08 13:15:00' },
    ],
  },
  {
    id: 10,
    ticketNumber: 'TKT-2026-0883',
    radioName: 'Radio Folclor Andino',
    frequency: '107.90 RFW',
    subject: 'Recuperar podcasts eliminados por error',
    description: 'El administrador anterior eliminó accidentalmente 3 episodios de nuestro podcast semanal. ¿Hay forma de recuperarlos del backup?',
    priority: 'normal',
    status: 'in_progress',
    assignedTo: 'SA-001',
    category: 'content',
    createdAt: '2026-06-06 15:45:00',
    updatedAt: '2026-06-08 10:30:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Folclor Andino', content: 'Hola, tuvimos un problema con el admin anterior que borró 3 episodios del podcast "Voces del Ande". Son los episodios del 15, 22 y 29 de mayo. ¿Tienen backup? Es contenido muy valioso para nuestra audiencia.', date: '2026-06-06 15:45:00' },
      { id: 2, from: 'support', author: 'Carlos Mendoza', content: 'Hola, revisé los backups y encontré los 3 episodios. Están intactos. Voy a restaurarlos en tu cuenta en las próximas horas. Te aviso cuando estén listos.', date: '2026-06-08 10:30:00' },
    ],
  },
  {
    id: 11,
    ticketNumber: 'TKT-2026-0882',
    radioName: 'Radio Éxitos TV',
    frequency: '100.00 RFW',
    subject: 'Integración con redes sociales no funciona',
    description: 'Intenté conectar nuestra cuenta de Instagram pero el botón de conectar no hace nada. Ya probé en Safari y Chrome.',
    priority: 'normal',
    status: 'resolved',
    assignedTo: 'SA-004',
    category: 'technical',
    createdAt: '2026-06-04 12:00:00',
    updatedAt: '2026-06-05 16:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Éxitos TV', content: 'Hola, el botón de conectar Instagram en Social Connect no funciona. Hago clic y no pasa nada. ¿Es un bug conocido?', date: '2026-06-04 12:00:00' },
      { id: 2, from: 'support', author: 'Diana Ríos', content: 'Hola, revisamos y era un problema de permisos de popup. Ya lo corregimos. Probá de nuevo, deberías ver la ventana de autorización de Instagram.', date: '2026-06-05 16:00:00' },
    ],
  },
  {
    id: 12,
    ticketNumber: 'TKT-2026-0881',
    radioName: 'Radio Sonora Digital',
    frequency: '96.20 RFW',
    subject: 'No recibo el email de verificación',
    description: 'Me registré hace 3 días y nunca llegó el email de verificación de cuenta. Revisé spam y todo.',
    priority: 'normal',
    status: 'open',
    assignedTo: null,
    category: 'account',
    createdAt: '2026-06-08 18:30:00',
    updatedAt: '2026-06-08 18:30:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Sonora Digital', content: 'Hola, me registré el 5 de junio y todavía no recibí el email de verificación. Ya revisé spam, correo no deseado, todo. ¿Pueden reenviarlo o verificarme manualmente?', date: '2026-06-08 18:30:00' },
    ],
  },
  {
    id: 13,
    ticketNumber: 'TKT-2026-0880',
    radioName: 'Radio Retro Hits',
    frequency: '93.10 RFW',
    subject: 'Apelación de suspensión',
    description: 'Nuestra radio fue suspendida por "contenido no autorizado" pero tenemos todas las licencias. Adjunto documentación.',
    priority: 'high',
    status: 'in_progress',
    assignedTo: 'SA-001',
    category: 'moderation',
    createdAt: '2026-06-05 08:00:00',
    updatedAt: '2026-06-07 14:20:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Retro Hits', content: 'Nos suspendieron la radio por supuesto contenido no autorizado. Adjunto todas las licencias de ASCAP, BMI y SESAC. Es un error, necesitamos que revisen urgente.', date: '2026-06-05 08:00:00' },
      { id: 2, from: 'support', author: 'Carlos Mendoza', content: 'Recibí la documentación. Estoy revisando con el equipo legal. En principio las licencias parecen válidas. Te respondo en 24-48h con la resolución.', date: '2026-06-07 14:20:00' },
    ],
  },
  {
    id: 14,
    ticketNumber: 'TKT-2026-0879',
    radioName: 'Radio Fusión Latina',
    frequency: '105.30 RFW',
    subject: 'Quiero cancelar mi cuenta',
    description: 'Por motivos personales necesitamos dar de baja la radio. ¿Cuál es el procedimiento? ¿Hay penalización?',
    priority: 'low',
    status: 'closed',
    assignedTo: 'SA-005',
    category: 'account',
    createdAt: '2026-06-03 10:00:00',
    updatedAt: '2026-06-05 12:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Fusión Latina', content: 'Hola, queremos cancelar el servicio. ¿Qué tenemos que hacer?', date: '2026-06-03 10:00:00' },
      { id: 2, from: 'support', author: 'Luis Hernández', content: 'Hola, lamento que se vayan. No hay penalización. Desde Ajustes > Cuenta > Eliminar radio pueden hacerlo. Se eliminará todo el contenido en 30 días. Si cambian de opinión, pueden reactivar dentro de ese plazo.', date: '2026-06-05 12:00:00' },
    ],
  },
  {
    id: 15,
    ticketNumber: 'TKT-2026-0878',
    radioName: 'Radio Vibra FM',
    frequency: '90.80 RFW',
    subject: 'El reproductor no carga en móviles',
    description: 'Varios oyentes reportan que en iPhone (Safari) el reproductor se queda en "Cargando..." y nunca arranca. En Android funciona bien.',
    priority: 'high',
    status: 'open',
    assignedTo: null,
    category: 'technical',
    createdAt: '2026-06-09 08:00:00',
    updatedAt: '2026-06-09 08:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Vibra FM', content: 'Hola, tenemos muchas quejas de oyentes con iPhone. El reproductor web se queda cargando infinitamente. Probamos en 3 iPhones distintos (iOS 18) y todos igual. En Android y desktop funciona perfecto.', date: '2026-06-09 08:00:00' },
    ],
  },
  {
    id: 16,
    ticketNumber: 'TKT-2026-0877',
    radioName: 'Stereo Visión',
    frequency: '94.70 RFW',
    subject: 'Estadísticas de analytics no se actualizan',
    description: 'El panel de analytics muestra los mismos números desde hace 4 días. Las métricas no reflejan la actividad real.',
    priority: 'normal',
    status: 'open',
    assignedTo: null,
    category: 'technical',
    createdAt: '2026-06-08 22:00:00',
    updatedAt: '2026-06-08 22:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Stereo Visión', content: 'Hola, desde el 5 de junio las estadísticas están congeladas. Mismo número de oyentes, mismas reproducciones. Claramente no es real porque tuvimos un pico el sábado. ¿Se rompió algo?', date: '2026-06-08 22:00:00' },
    ],
  },
  {
    id: 17,
    ticketNumber: 'TKT-2026-0876',
    radioName: 'Radio Nueva Actitud',
    frequency: '87.90 RFW',
    subject: 'Solicitud de feature: encuestas en vivo',
    description: 'Nos encantaría tener una herramienta de encuestas en vivo para interactuar con los oyentes durante los programas. ¿Está en el roadmap?',
    priority: 'low',
    status: 'resolved',
    assignedTo: 'SA-006',
    category: 'other',
    createdAt: '2026-06-02 14:00:00',
    updatedAt: '2026-06-04 11:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Nueva Actitud', content: 'Hola equipo, sería genial tener encuestas en vivo para que los oyentes voten durante el programa. ¿Tienen planeado algo así?', date: '2026-06-02 14:00:00' },
      { id: 2, from: 'support', author: 'Sofía Vargas', content: 'Hola, ¡buena idea! Las encuestas en vivo están en nuestro roadmap para Q3 2026. Mientras tanto, podés usar la cabina para hacer preguntas y que la gente responda por chat. Tomo nota de tu solicitud.', date: '2026-06-04 11:00:00' },
    ],
  },
  {
    id: 18,
    ticketNumber: 'TKT-2026-0875',
    radioName: 'Radio Deportiva Total',
    frequency: '101.70 RFW',
    subject: 'Problema con el contador de oyentes',
    description: 'El contador de oyentes en vivo marca 0 aunque sabemos que hay gente escuchando. ¿Es un bug visual o no está contando?',
    priority: 'normal',
    status: 'closed',
    assignedTo: 'SA-003',
    category: 'technical',
    createdAt: '2026-06-01 16:00:00',
    updatedAt: '2026-06-03 10:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Deportiva Total', content: 'Hola, el contador de oyentes marca 0 todo el tiempo. Sé que hay gente escuchando porque me escriben por WhatsApp. ¿Está roto?', date: '2026-06-01 16:00:00' },
      { id: 2, from: 'support', author: 'Andrés López', content: 'Hola, detectamos que tu stream tenía un formato de metadata no estándar. Lo ajustamos y el contador ya debería funcionar. ¿Podés confirmar?', date: '2026-06-03 10:00:00' },
    ],
  },
  {
    id: 19,
    ticketNumber: 'TKT-2026-0874',
    radioName: 'Radio Urbana 101',
    frequency: '103.50 RFW',
    subject: 'Fotos no se muestran en la galería',
    description: 'Subí 12 fotos a la galería pero solo se ven 3. Las otras aparecen como "procesando" hace 2 días.',
    priority: 'normal',
    status: 'waiting_customer',
    assignedTo: 'SA-004',
    category: 'content',
    createdAt: '2026-06-06 09:30:00',
    updatedAt: '2026-06-07 15:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Radio Urbana 101', content: 'Subí fotos de nuestro evento del finde y la mayoría no se procesan. Solo se ven 3 de 12.', date: '2026-06-06 09:30:00' },
      { id: 2, from: 'support', author: 'Diana Ríos', content: 'Hola, revisé las 9 fotas que no procesaron y veo que son archivos HEIC de iPhone. Nuestro sistema no soporta HEIC directamente. ¿Podés convertirlas a JPG o PNG y volver a subirlas?', date: '2026-06-07 15:00:00' },
    ],
  },
  {
    id: 20,
    ticketNumber: 'TKT-2026-0873',
    radioName: 'Clásica FM',
    frequency: '99.10 RFW',
    subject: 'No puedo eliminar un programa de la grilla',
    description: 'Hay un programa viejo en la grilla que no me deja eliminar. El botón de eliminar está gris.',
    priority: 'low',
    status: 'open',
    assignedTo: null,
    category: 'content',
    createdAt: '2026-06-09 06:00:00',
    updatedAt: '2026-06-09 06:00:00',
    messages: [
      { id: 1, from: 'customer', author: 'Admin Clásica FM', content: 'Hola, quiero limpiar la grilla de programas viejos pero hay uno que no se puede eliminar. El botón aparece deshabilitado.', date: '2026-06-09 06:00:00' },
    ],
  },
];